import { useState, useEffect } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const sortModels = (models) =>
  [...(models || [])].sort((a, b) => b.generation - a.generation || b.name.localeCompare(a.name));

const useIsMobile = () => {
  const [m, setM] = useState(() => (typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false));
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = (e) => setM(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return m;
};

export const ModelCombobox = ({
  value,
  onChange,
  models,
  disabled = false,
  placeholder = "Seleccionar modelo",
  testid = "model-combobox",
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const isMobile = useIsMobile();
  const list = sortModels(models);

  const triggerClass = cn(
    "flex h-10 w-full items-center justify-between rounded-xl border border-zinc-200 bg-white px-3 text-sm transition-colors hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 disabled:opacity-50 disabled:cursor-not-allowed",
    !value && "text-zinc-400",
    className
  );

  // ---------- MÓVIL: Dialog anidado a pantalla completa (Radix gestiona foco/puntero) ----------
  if (isMobile) {
    const filtered = q.trim() ? list.filter((m) => m.name.toLowerCase().includes(q.toLowerCase().trim())) : list;
    return (
      <>
        <button type="button" role="combobox" aria-expanded={open} disabled={disabled} data-testid={testid}
          onClick={() => { setQ(""); setOpen(true); }} className={triggerClass}>
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            onOpenAutoFocus={(e) => e.preventDefault()}
            data-testid={`${testid}-sheet`}
            className="left-0 top-0 h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 flex flex-col gap-0 rounded-none border-0 p-0"
          >
            <DialogHeader className="shrink-0 border-b border-zinc-200 px-4 py-3 text-left">
              <DialogTitle>Seleccionar modelo</DialogTitle>
              <DialogDescription className="sr-only">Busca y selecciona el modelo de iPhone</DialogDescription>
            </DialogHeader>
            <div className="shrink-0 px-4 py-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                <input type="text" inputMode="search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar modelo..." data-testid={`${testid}-search`}
                  className="h-11 w-full rounded-xl border border-zinc-200 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10" />
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-6 [touch-action:pan-y] [-webkit-overflow-scrolling:touch]" data-testid={`${testid}-list`}>
              {filtered.length === 0 ? (
                <p className="py-10 text-center text-sm text-zinc-400">Sin resultados</p>
              ) : filtered.map((m) => (
                <button key={m.name} type="button" data-testid={`${testid}-option-${m.name}`}
                  onClick={() => { onChange(m.name); setOpen(false); }}
                  className="flex w-full items-center gap-2 rounded-xl px-3 py-3.5 text-left text-sm hover:bg-zinc-50 active:bg-zinc-100">
                  <Check className={cn("h-4 w-4 shrink-0", value === m.name ? "opacity-100 text-emerald-600" : "opacity-0")} />
                  <span>{m.name}</span>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // ---------- DESKTOP: comportamiento actual (Popover) ----------
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open} disabled={disabled} data-testid={testid} className={triggerClass}>
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6}
        className="min-w-[--radix-popover-trigger-width] w-max max-w-[calc(100vw-2rem)] p-0"
        data-testid={`${testid}-panel`}>
        <Command filter={(v, search) => (v.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0)}>
          <CommandInput placeholder="Buscar modelo..." data-testid={`${testid}-search`} />
          <CommandList className="max-h-[min(320px,60vh)] overscroll-contain [touch-action:pan-y]">
            <CommandEmpty>Sin resultados</CommandEmpty>
            {list.map((m) => (
              <CommandItem key={m.name} value={m.name} onSelect={() => { onChange(m.name); setOpen(false); }}
                className="py-2.5 cursor-pointer" data-testid={`${testid}-option-${m.name}`}>
                <Check className={cn("mr-2 h-4 w-4 shrink-0", value === m.name ? "opacity-100" : "opacity-0")} />
                <span className="whitespace-nowrap">{m.name}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
